import { STORAGE_THEME } from '../constants/storageConst';
import { getThemeColors } from '../services/themeService';

export const MODE_LIST = [
  { label: 'Dark', value: 'dark' },
  { label: 'Light', value: 'light' },
  { label: 'Custom', value: 'custom' }
];
export const DARK_MODE = MODE_LIST?.[0]?.value;
export const LIGHT_MODE = MODE_LIST?.[1]?.value;

export const getLightness = ({ color, mode }) => {
  if (color && color !== null) {
    if (color[0] === '#') {
      color = color.slice(1);
    }

    const bigint = parseInt(color, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;

    const normalizedR = r / 255;
    const normalizedG = g / 255;
    const normalizedB = b / 255;

    // Calculate lightness using simple average of normalized values
    const lightness = (normalizedR + normalizedG + normalizedB) / 3;

    if (mode === LIGHT_MODE) {
      if (lightness <= 0.5) {
        return 'var(--black)';
      }
      return 'var(--white)';
    }

    if (lightness <= 0.5) {
      return 'var(--white)';
    }
    return 'var(--black)';
  } else {
    return 'var(--black)';
  }
};
function adjustColorShade({ color, percentage }) {
  let r = parseInt(color.slice(1, 3), 16);
  let g = parseInt(color.slice(3, 5), 16);
  let b = parseInt(color.slice(5, 7), 16);

  if (percentage > 0) {
    // Lighten color by blending it with white
    r += ((255 - r) * percentage) / 100;
    g += ((255 - g) * percentage) / 100;
    b += ((255 - b) * percentage) / 100;
  } else {
    // Darken color by blending it with black
    r += (r * percentage) / 100;
    g += (g * percentage) / 100;
    b += (b * percentage) / 100;
  }

  r = Math.round(r);
  g = Math.round(g);
  b = Math.round(b);

  return `#${(r < 16 ? '0' : '') + r.toString(16)}${(g < 16 ? '0' : '') + g.toString(16)}${
    (b < 16 ? '0' : '') + b.toString(16)
  }`;
}
function adjustColorShadeLightMode({ color, percentage }) {
  let r = parseInt(color.slice(1, 3), 16);
  let g = parseInt(color.slice(3, 5), 16);
  let b = parseInt(color.slice(5, 7), 16);

  if (percentage > 0) {
    // Darken color by blending it with black
    r -= (r * percentage) / 100;
    g -= (g * percentage) / 100;
    b -= (b * percentage) / 100;
  } else {
    // Lighten color by blending it with white
    r += ((255 - r) * Math.abs(percentage)) / 100;
    g += ((255 - g) * Math.abs(percentage)) / 100;
    b += ((255 - b) * Math.abs(percentage)) / 100;
  }

  r = Math.max(0, Math.min(255, Math.round(r)));
  g = Math.max(0, Math.min(255, Math.round(g)));
  b = Math.max(0, Math.min(255, Math.round(b)));

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b
    .toString(16)
    .padStart(2, '0')}`;
}

export const variableAdding = (
  theme_colorname,
  theme_colorvalue,
  theme_generateshade,
  completeThemeSetArr,
  mode
) => {
  let appendRootStr = `--${theme_colorname}: ${theme_colorvalue};`;
  const shadePerArr = [
    { addPostfix: 100, shadePercentage: 70 },
    { addPostfix: 200, shadePercentage: 50 },
    { addPostfix: 300, shadePercentage: 30 },
    { addPostfix: 400, shadePercentage: 15 },
    { addPostfix: 500, shadePercentage: 0 },
    { addPostfix: 600, shadePercentage: -15 },
    { addPostfix: 700, shadePercentage: -30 },
    { addPostfix: 800, shadePercentage: -40 },
    { addPostfix: 900, shadePercentage: -50 },
    { addPostfix: 1000, shadePercentage: -70 }
  ];
  if (theme_generateshade) {
    shadePerArr.map(item => {
      const { addPostfix, shadePercentage } = item;
      const key = `${theme_colorname}-${addPostfix}`;

      if (addPostfix < 500) {
        let shadedColor;
        if (mode === LIGHT_MODE) {
          shadedColor = adjustColorShadeLightMode({
            color: theme_colorvalue,
            percentage: shadePercentage,
            mode
          });
        } else {
          shadedColor = adjustColorShade({ color: theme_colorvalue, percentage: shadePercentage, mode });
        }
        appendRootStr += `--${key}: ${shadedColor};`;
        completeThemeSetArr.push({ theme_colorname: key, theme_colorvalue: shadedColor });
      }
      if (addPostfix === 500) {
        appendRootStr += `--${key}: ${theme_colorvalue};`;
        completeThemeSetArr.push({
          theme_colorname: key,
          theme_colorvalue: theme_colorvalue
        });
      }
      if (addPostfix > 500) {
        let shadedColor;
        if (mode === LIGHT_MODE) {
          shadedColor = adjustColorShadeLightMode({
            color: theme_colorvalue,
            percentage: shadePercentage,
            mode
          });
        } else {
          shadedColor = adjustColorShade({ color: theme_colorvalue, percentage: shadePercentage, mode });
        }
        appendRootStr += `--${key}: ${shadedColor};`;
        completeThemeSetArr.push({
          theme_colorname: key,
          theme_colorvalue: shadedColor
        });
      }
    });
  }
  return appendRootStr;
};

export const buttonStyles = ({ theme_colorname, theme_colorvalue, mode }) => {
  let getVarColor = `var(--${theme_colorname})`;
  let hoverBackground = `var(--${theme_colorname}-900) !important`;
  let hoverBorder = `var(--${theme_colorname}-400) !important;`;
  let boxShadowColor = `0 0 0 0.2rem var(--${theme_colorname}-800) !important`;
  let applyColor = getLightness({ color: theme_colorvalue, mode });

  const buttonStylesString = `
    .btn-${theme_colorname} {
      color: ${applyColor} !important;
      background-color: ${getVarColor};
      border-color: ${getVarColor};
      &:hover {
        fill: ${applyColor} !important;
        color: ${applyColor} !important;
        background-color: ${hoverBackground};
        border-color: ${hoverBorder};
      }
      &:focus {
        box-shadow: ${boxShadowColor};
      }
      &:hover svg path, &:focus svg path {
        fill: ${applyColor} !important;
      }
      &:disabled {
        color: ${applyColor} !important;
        background-color: ${getVarColor} !important;
        border-color: ${getVarColor} !important;
      }
      &:not(:disabled):not(.disabled).active, &:not(:disabled):not(.disabled):active {
        color: ${applyColor} !important;
        background-color: ${hoverBackground};
        border-color: ${hoverBorder};
        &:hover {
          box-shadow: 0 0 0 1px transparent, 0 0 0 0.15rem ${hoverBorder};
        }
        &:focus {
          box-shadow: ${boxShadowColor};
        }
      }
    }
  `;
  return buttonStylesString;
};

export const buttonOutlineStyles = ({ theme_colorname, theme_colorvalue, mode }) => {
  let getVarColor = `var(--${theme_colorname})`;
  let boxShadowColor = `0 0 0 0.2rem var(--${theme_colorname}-800) !important`;
  let applyColor = getLightness({ color: theme_colorvalue, mode });

  const buttonOutlineStylesString = `
    .btn-outline-${theme_colorname} {
      color: ${getVarColor} !important;
      border-color: ${getVarColor} !important;
      &:hover {
        color: ${applyColor} !important;
        fill: ${applyColor} !important;
        background-color: ${getVarColor} !important;
        border-color: ${getVarColor} !important;
      }
      &:focus {
        box-shadow: ${boxShadowColor};
      }
      &:hover svg path, &:focus svg path {
        fill: ${applyColor};
      }
      &:disabled {
        color: ${getVarColor} !important;
        background-color: transparent !important;
      }
      &:not(:disabled):not(.disabled).active, &:not(:disabled):not(.disabled):active {
        color: ${applyColor} !important;
        background-color: ${getVarColor} !important;
        border-color: ${getVarColor} !important;
        &:hover {
          box-shadow: 0 0 0 1px transparent, 0 0 0 0.15rem ${boxShadowColor};
        }
        &:focus {
          box-shadow: ${boxShadowColor};
        }
      }
    }
  `;
  return buttonOutlineStylesString;
};

export const textBgStyles = ({ theme_colorname }) => {
  let getVarColor = `var(--${theme_colorname})`;
  let textBgStyleString = `
    .text-${theme_colorname} {
      color: ${getVarColor} !important;
      fill: ${getVarColor} !important;
    }
    .bg-${theme_colorname} {
      background-color: ${getVarColor} !important;
    }
  `;
  return textBgStyleString;
};

export const badgeStyles = ({ theme_colorname, theme_colorvalue, mode }) => {
  let getVarColor = `var(--${theme_colorname})`;
  let boxShadowColor = `0 0 0 0.1rem var(--${theme_colorname}-800)`;
  let badgeStyleString = `
    .badge-${theme_colorname}, a.badge-${theme_colorname} {
      background-color: ${getVarColor} !important;
      color: ${getLightness({ color: theme_colorvalue, mode })};

      &:focus {
        background-color: var(--${theme_colorname}-600);
        outline: 0;
        box-shadow: ${boxShadowColor};
        text-decoration: none !important;
      }
    }
  `;
  return badgeStyleString;
};

export const borderColorStyles = ({ theme_colorname }) => {
  let getVarColor = `var(--${theme_colorname})`;
  let borderColorStyleString = `
    .b-${theme_colorname} {
      border-color: ${getVarColor} !important;
      border-style: solid;
      border-width: 1px;
    }
    .bt-${theme_colorname} {
      border-top-color: ${getVarColor} !important;
      border-top-style: solid;
      border-top-width: 1px;
    }
    .by-${theme_colorname} {
      border-top-color: ${getVarColor} !important;
      border-top-style: solid;
      border-top-width: 1px;
      border-bottom-color: ${getVarColor} !important;
      border-bottom-style: solid;
      border-bottom-width: 1px;
    }
    .br-${theme_colorname} {
      border-right-color: ${getVarColor} !important;
      border-right-style: solid;
      border-right-width: 1px;
    }
    .bx-${theme_colorname} {
      border-right-color: ${getVarColor} !important;
      border-right-style: solid;
      border-right-width: 1px;
      border-left-color: ${getVarColor} !important;
      border-left-style: solid;
      border-left-width: 1px;
    }
    .bb-${theme_colorname} {
      border-bottom-color: ${getVarColor} !important;
      border-bottom-style: solid;
      border-bottom-width: 1px;
    }
    .bl-${theme_colorname} {
      border-left-color: ${getVarColor} !important;
      border-left-style: solid;
      border-left-width: 1px;
    }
  `;
  return borderColorStyleString;
};

export const makeDynamicStyle = async ({ user_id }) => {
  let baseThemeColorArr = [];
  let mode = DARK_MODE;
  let appendRootStr = '';
  let buttonStyleString = '';
  let buttonOutlineStyleString = '';
  let textBgStyleString = '';
  let badgeStyleString = '';
  let borderColorStyleString = '';
  let isLightTheme = false;

  const data = await getThemeColors({ user_id });
  if (data?.status) {
    const obj = data?.data?.[0];
    mode = obj?.mode ?? DARK_MODE;
    isLightTheme = mode === LIGHT_MODE;
    baseThemeColorArr = obj?.userThemeColors ?? [];
  }

  const completeThemeSetArr = [...baseThemeColorArr];
  const primaryColorValue = completeThemeSetArr?.find(x => x.theme_colorname === 'primary')?.theme_colorvalue;
  const darkColorValue = completeThemeSetArr?.find(x => x.theme_colorname === 'dark')?.theme_colorvalue;

  appendRootStr += `
    --theme_font_color: ${getLightness({
      color: primaryColorValue,
      mode
    })};
    --font_color: ${getLightness({
      color: darkColorValue,
      mode
    })};
  `;

  let theme_font_color = getLightness({ color: primaryColorValue, mode });
  let font_color = getLightness({ color: darkColorValue, mode });

  baseThemeColorArr.map(originalColor => {
    const { theme_colorname, theme_colorvalue, theme_generateshade } = originalColor;
    appendRootStr += variableAdding(
      theme_colorname,
      theme_colorvalue,
      theme_generateshade,
      completeThemeSetArr,
      mode
    );
    buttonStyleString += buttonStyles({ theme_colorname, theme_colorvalue, mode });
    buttonOutlineStyleString += buttonOutlineStyles({ theme_colorname, theme_colorvalue, mode });
    badgeStyleString += badgeStyles({ theme_colorname, theme_colorvalue, mode });
    borderColorStyleString += borderColorStyles({ theme_colorname, theme_colorvalue, mode });
  });

  const themeColorObj = {};
  completeThemeSetArr.forEach(item => {
    const { theme_colorname, theme_colorvalue } = item;
    themeColorObj[theme_colorname] = theme_colorvalue;
    buttonStyleString += buttonStyles({ theme_colorname, theme_colorvalue, mode });
    buttonOutlineStyleString += buttonOutlineStyles({ theme_colorname, theme_colorvalue, mode });
    badgeStyleString += badgeStyles({ theme_colorname, theme_colorvalue, mode });
    borderColorStyleString += borderColorStyles({ theme_colorname, theme_colorvalue, mode });
    textBgStyleString += textBgStyles({ theme_colorname, theme_colorvalue });
  });

  let toastStyleString = `
    :root {
      --toastify-color-success: var(--success);
      --toastify-color-info: var(--info);
      --toastify-color-warning: var(--warning);
      --toastify-color-error: var(--danger);
    }
  `;

  const outputRes = {
    mode,
    isLightTheme,
    baseThemeColorArr,
    completeThemeSetArr,
    themeColorObj,
    appendRootStr,
    theme_font_color,
    font_color,
    buttonStyleString,
    buttonOutlineStyleString,
    textBgStyleString,
    badgeStyleString,
    borderColorStyleString,
    toastStyleString
  };
  localStorage.setItem(STORAGE_THEME, JSON.stringify(outputRes));
  return outputRes;
};
